import { store$, store } from '@redux/store';
import { combineLatest, distinctUntilChanged, filter, map, merge, skip, tap, withLatestFrom } from 'rxjs';
import {
  isNotCommunityLocationType,
  moveInDateSelector,
  selectFacts,
  selectFeatures,
  selectLocation,
  selectProduct,
  selectResults,
  selectResultsModifiers,
  isLocationTypeState
} from 'features/searchResultsFacets';
import LoadingStatus from 'enums/loadingStatus';
import { urlFilterSelector } from 'features/routing';
import { selectResultsPresentationType } from 'layout/redux/selectors';
import ResultPresentationType from 'layout/constants/resultPresentationType';
import initializeFaqAccordion from 'pages/searchResults/components/faqAccordionHandler';
import locationContentSlice from '../redux/slice';
import updateMarketOverviewStats from './marketOverviewStatsComponent';
import {
  selectCanonical,
  selectFetchStatus,
  selectFooter,
  selectHeading1,
  selectHeading2,
  selectMetaDescription,
  selectMetaRobots,
  selectTitle,
  selectMarketOverviewTitle,
  selectMarketOverviewContent,
  selectMarketOverviewStats,
  selectSpecialCommunities,
  selectFaqTitle,
  selectFaqContent,
  selectGuidesTitle,
  selectGuidesContent,
  selectArticleTitle,
  selectArticleContent,
  selectTopMarketsTitle
} from '../redux/selectors';

const h1Element = document.querySelector('[data-heading1]');
const h2Element = document.querySelector('[data-heading2]');
const footer = document.querySelector('[data-footer-content]');
const readMoreButton = document.querySelector('[data-read-more]');
const metaRobotsElement = document.querySelector('meta[name="robots"]');
const marketOverviewTitleElement = document.querySelector('[data-state-search-market-overview-title]');
const marketOverviewContentElement = document.querySelector('[data-state-search-market-overview-content]');
const marketOverviewStatsElement = document.querySelector('[data-state-search-market-overview-stats]');
const specialCommunitiesElement = document.querySelector('[data-state-search-special-communities-content]');
const faqTitleElement = document.querySelector('[data-state-search-faq-title]');
const faqContentElement = document.querySelector('[data-state-search-faq-content]');
const guidesTitleElement = document.querySelector('[data-state-search-guides-title]');
const guidesContentElement = document.querySelector('[data-state-search-guides-content]');
const articleTitleElement = document.querySelector('[data-state-search-article-title]');
const articleContentElement = document.querySelector('[data-state-search-article-content]');
const topMarketsTitleElement = document.querySelector('[data-state-search-top-markets-title]');

const fetchLocationContent = () => store.dispatch(locationContentSlice.actions.fetch());
const isNotFetchingContent = (state) => selectFetchStatus(state) !== LoadingStatus.Loading;

const updateMetaRobots = (metaRobots) => {
  metaRobotsElement.setAttribute('content', metaRobots);
};

/**
 * Insert the heading 1 when the Ajax request is triggered.
 * @param {any} data
 */
const updateH1Text = (heading1) => {
  h1Element.innerText = heading1;
};

/**
 * Insert the heading 2 when the Ajax request is triggered.
 * @param {any} data
 */
const updateH2Text = (heading2) => {
  h2Element.innerText = heading2;
};

/**
 * Update the title when the Ajax request is triggered.
 * @param {*} data
 */
const updateTitle = (title) => {
  document.title = title;
};

/**
 * Function used to remove the canonical if the move-in date is applied.
 */
const removeCanonicalOnMoveInDateFilterApplied = () => {
  const state = store.getState();
  const { moveInDateRange } = moveInDateSelector(state);

  const canonical = document.querySelector('link[rel="canonical"]');

  // NHS20-1365: If the move in date range is set, remove the canonical element
  if (moveInDateRange) {
    canonical?.remove();
  }
};

/**
 * Update the canonical when there already exist a tag element in the page.
 */
const updateCurrentCanonicalElement = (canonicalElement, canonicalUrl) => {
  canonicalElement.setAttribute('href', canonicalUrl);
};

/**
 * Creates the canonical tag when there is not a tag already in the page.
 */
const createCanonicalElement = (canonicalUrl) => {
  const newCanonicalElement = document.createElement('link');
  newCanonicalElement.setAttribute('rel', 'canonical');
  document.head.appendChild(newCanonicalElement);
  newCanonicalElement.setAttribute('href', canonicalUrl);
};

/**
 * Update the canonical when the Ajax request is triggered.
 */
const updateCanonical = (canonicalUrl) => {
  const canonical = document.querySelector('link[rel="canonical"]');
  
  // if the canonical element doesn't exist, create it
  if (canonical) {
    updateCurrentCanonicalElement(canonical, canonicalUrl);
  } else if (canonicalUrl) {
    createCanonicalElement(canonicalUrl);
  }
};

/**
 * Insert the Footer container and read more button when the Ajax request is triggered.
 * @param {any} data
 */
function updateFooter(footerData) {
  footer.innerHTML = footerData;

  if (footer.innerText.length < 600) {
    readMoreButton.style.display = 'none';
  } else {
    readMoreButton.style.display = 'block';
  }
}

function updateMetaDescription(metaDescriptionData) {
  const metaDescription = document.querySelector('meta[name="description"]');

  if (metaDescription) {
    metaDescription.setAttribute('content', metaDescriptionData);
  } else {
    const newMetaDescription = document.createElement('meta');
    newMetaDescription.setAttribute('name', 'description');
    document.head.appendChild(newMetaDescription);
    newMetaDescription.setAttribute('content', metaDescriptionData);
  }
}

function updateStateSearchElement(element, content = "", isTextContent = false, initFunction = null) {
  if (!element) { return; }
  const elementContainer = element.closest('[data-state-search-content-container]');
  if (isTextContent) {
    element.innerText = content;
  } else {
    element.innerHTML = content;
  }
  if (elementContainer) {
    elementContainer.hidden = !content;
  }
  if (initFunction) {
    initFunction();
  }
}

function updateStateSearchContent(state) {
  updateMarketOverviewStats(marketOverviewStatsElement, selectMarketOverviewStats(state));
  updateStateSearchElement(marketOverviewTitleElement, selectMarketOverviewTitle(state), true);
  updateStateSearchElement(marketOverviewContentElement, selectMarketOverviewContent(state), true);
  updateStateSearchElement(specialCommunitiesElement, selectSpecialCommunities(state));
  updateStateSearchElement(faqTitleElement, selectFaqTitle(state), true);
  updateStateSearchElement(faqContentElement, selectFaqContent(state), false, initializeFaqAccordion);
  updateStateSearchElement(guidesTitleElement, selectGuidesTitle(state), true);
  updateStateSearchElement(guidesContentElement, selectGuidesContent(state));
  updateStateSearchElement(articleTitleElement, selectArticleTitle(state), true);
  updateStateSearchElement(articleContentElement, selectArticleContent(state));
  updateStateSearchElement(topMarketsTitleElement, selectTopMarketsTitle(state), true);
}

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in results leaf change, fetch the housing product locaction content.
 */
const results$ = store$.pipe(filter(isNotFetchingContent), map(selectResults), distinctUntilChanged());

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in facts leaf change, fetch the housing product locaction content.
 */
const facts$ = store$.pipe(filter(isNotFetchingContent), map(selectFacts), distinctUntilChanged());

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in features leaf change, fetch the housing product locaction content.
 */
const features$ = store$.pipe(filter(isNotFetchingContent), map(selectFeatures), distinctUntilChanged());

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in location leaf change, fetch the housing product locaction content.
 */
const location$ = store$.pipe(
  filter(isNotFetchingContent),
  filter(isNotCommunityLocationType),
  map(selectLocation),
  distinctUntilChanged()
);

const presentationType$ = store$.pipe(map(selectResultsPresentationType), distinctUntilChanged());

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in product leaf change, fetch the housing product locaction content.
 */
const product$ = store$.pipe(filter(isNotFetchingContent), map(selectProduct), distinctUntilChanged());

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in results modifier leaf change, fetch the housing product locaction content.
 */
const resultsModifier$ = store$.pipe(filter(isNotFetchingContent), map(selectResultsModifiers), distinctUntilChanged());

/** Subscribe to the store$ observable to listen for changes
 * When the URL filter change, fetch the housing product locaction content. We need to check if this URL filter change
 * beacuse the @HousingProductLocationContentCommand class is using this selector to send the data to the backend
 * and if it is updated it we have issues with the canonical.
 */
const onUrlFilterChange$ = store$.pipe(filter(isNotFetchingContent), map(urlFilterSelector), distinctUntilChanged());

combineLatest([results$, product$, location$, features$, resultsModifier$, facts$, onUrlFilterChange$])
  .pipe(skip(1),
    withLatestFrom(presentationType$),
    filter(([combinedValues, presentationType]) => {
      const [results] = combinedValues;
      
      const isInfiniteScrolling = presentationType === ResultPresentationType.InfiniteScrolling.toString();
      
      // If presentation type is InfiniteScrolling and page is greater than 1, we don't need to trigger the
      // ajax request since the location content was already retrieved for page 1 and won't change.
      return !(isInfiniteScrolling && results.paging.page > 1);
    })
  ).subscribe(fetchLocationContent);

// We subscribe the methods to handle the html elements.
const setH1Text$ = store$.pipe(
  map(selectHeading1),
  distinctUntilChanged(),
  filter(() => !!h1Element),
  skip(1),
  tap(updateH1Text)
);

const setH2Text$ = store$.pipe(
  map(selectHeading2),
  distinctUntilChanged(),
  filter(() => !!h2Element),
  skip(1),
  tap(updateH2Text)
);

const setFooter$ = store$.pipe(
  map(selectFooter),
  distinctUntilChanged(),
  filter(() => !!footer && !!readMoreButton),
  skip(1),
  tap(updateFooter)
);

const setTitle$ = store$.pipe(
  map(selectTitle),
  distinctUntilChanged(),
  filter(() => !!document.title),
  skip(1),
  tap(updateTitle)
);

const setStateSearchContent$ = store$.pipe(
  filter(isLocationTypeState),
  distinctUntilChanged((previous, current) =>
    selectMarketOverviewTitle(previous) === selectMarketOverviewTitle(current) &&
    selectMarketOverviewContent(previous) === selectMarketOverviewContent(current) &&
    selectMarketOverviewStats(previous) === selectMarketOverviewStats(current) &&
    selectSpecialCommunities(previous) === selectSpecialCommunities(current) &&
    selectFaqTitle(previous) === selectFaqTitle(current) &&
    selectFaqContent(previous) === selectFaqContent(current) &&
    selectGuidesTitle(previous) === selectGuidesTitle(current) &&
    selectGuidesContent(previous) === selectGuidesContent(current) &&
    selectArticleTitle(previous) === selectArticleTitle(current) &&
    selectArticleContent(previous) === selectArticleContent(current) &&
    selectTopMarketsTitle(previous) === selectTopMarketsTitle(current)
  ),
  skip(1),
  tap(updateStateSearchContent)
);

const setCanonical$ = store$.pipe(map(selectCanonical), distinctUntilChanged(), skip(1), tap(updateCanonical));

const setMetaRobots$ = store$.pipe(map(selectMetaRobots), distinctUntilChanged(), skip(1), tap(updateMetaRobots));

const setMetaDescription$ = store$.pipe(
  map(selectMetaDescription),
  distinctUntilChanged(),
  skip(1),
  tap(updateMetaDescription)
);

merge(
  setH1Text$, setH2Text$, setFooter$, setTitle$, setCanonical$,
  setMetaDescription$, setMetaRobots$, setStateSearchContent$
).subscribe();
removeCanonicalOnMoveInDateFilterApplied();
